import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, ReceiptText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function Receipts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ merchant_name: "", amount: 0, date: new Date().toISOString().slice(0, 10) });

  const load = async () => {
    const { data } = await api.get("/lifestyle/receipts");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.merchant_name || !form.amount) return;
    try {
      await api.post("/lifestyle/receipts", { ...form, amount: parseFloat(form.amount) });
      toast.success("Receipt submitted for review");
      setForm({ merchant_name: "", amount: 0, date: new Date().toISOString().slice(0, 10) });
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Couldn't submit");
    }
  };

  return (
    <div data-testid="page-receipts">
      <PageHeader
        eyebrow="Missed cashback?"
        title="Upload a receipt"
        subtitle="If you forgot to activate cashback, submit your receipt and we'll review it with the merchant."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="add-receipt-btn"><Plus className="w-4 h-4 mr-2" /> Submit receipt</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Submit a receipt</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label className="mb-1.5 block">Merchant</Label><Input value={form.merchant_name} onChange={(e) => setForm({ ...form, merchant_name: e.target.value })} placeholder="Walmart" data-testid="receipt-merchant" /></div>
                <div><Label className="mb-1.5 block">Amount ($)</Label><Input type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="receipt-amount" /></div>
                <div><Label className="mb-1.5 block">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <p className="text-xs text-gray-500">Receipt image upload is coming soon. For now, we'll review your claim against the merchant's affiliate data.</p>
              </div>
              <DialogFooter><Button onClick={submit} className="bg-emerald-700 hover:bg-emerald-800 text-white">Submit</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="border-gray-200 shadow-none rounded-2xl">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr className="text-left">
                <th className="px-5 py-3 font-semibold">Merchant</th>
                <th className="px-5 py-3 font-semibold">Amount</th>
                <th className="px-5 py-3 font-semibold">Date</th>
                <th className="px-5 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-gray-100" data-testid={`receipt-${r.id}`}>
                  <td className="px-5 py-3 font-medium">{r.merchant_name}</td>
                  <td className="px-5 py-3">{fmtMoney(r.amount)}</td>
                  <td className="px-5 py-3 text-gray-500">{r.date}</td>
                  <td className="px-5 py-3"><Badge className="bg-amber-50 text-amber-800 border border-amber-200">{r.status}</Badge></td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="4" className="px-5 py-12 text-center">
                  <ReceiptText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <div className="text-gray-500">No receipts submitted yet.</div>
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
